import { useState } from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerBody,
  DrawerFooter,
  Button,
  Input,
  Textarea,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import { useCreateInvoiceTransaction, type InvoiceDetail } from '@/api/invoices';
import { DateInput } from '@/components/date-input';
import { NumericInput } from '@/components/numeric-input';

/** Build a local `YYYY-MM-DD` (no UTC shift — see ui-conventions §4). */
function todayLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const PAYMENT_METHODS = [
  { value: 'bank-transfer', label: 'Bank transfer' },
  { value: 'cash', label: 'Cash' },
  { value: 'credit', label: 'Credit' },
  { value: 'other', label: 'Other' },
] as const;

function errorMessage(err: unknown): string {
  const res = (err as { response?: { data?: { error_description?: string; error?: string; message?: string } } })
    ?.response;
  return res?.data?.error_description ?? res?.data?.error ?? res?.data?.message ?? 'Something went wrong.';
}

/**
 * Mark-Paid drawer — records a payment against the invoice via
 * `POST /invoices/:id/transactions`. Amount is entered *before* exchange rate;
 * the VND-equivalent `transaction_amount` is `amount × rate`. The USER submits
 * the live create; the assistant only builds + wires the form.
 */
export function InvoiceMarkPaidDrawer({
  id,
  detail,
  open,
  onOpenChange,
}: {
  id: number;
  detail: InvoiceDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const create = useCreateInvoiceTransaction(id);
  const invoiceCurrency = (detail as { invoice_currency?: string }).invoice_currency ?? 'VND';

  const [method, setMethod] = useState<string>('bank-transfer');
  const [amount, setAmount] = useState<string>(String(detail.invoice_total ?? ''));
  const [currency, setCurrency] = useState<string>(invoiceCurrency);
  const [rate, setRate] = useState<string>('1');
  const [paymentAt, setPaymentAt] = useState<string>(todayLocal());
  const [fee, setFee] = useState<string>('0');
  const [note, setNote] = useState<string>('');

  const reset = () => {
    setMethod('bank-transfer');
    setAmount(String(detail.invoice_total ?? ''));
    setCurrency(invoiceCurrency);
    setRate('1');
    setPaymentAt(todayLocal());
    setFee('0');
    setNote('');
    create.reset();
  };

  const submit = () => {
    const before = parseFloat(amount);
    const exRate = parseFloat(rate) || 1;
    if (!before || before <= 0) return;
    create.mutate(
      {
        transaction_amount: before * exRate,
        invoice_transaction_before_exchange_rate: before,
        transaction_method: method,
        transaction_fee: parseFloat(fee) || 0,
        transaction_status: 'completed',
        transaction_note: note.trim() || undefined,
        exchange_rate: exRate,
        currency,
        currency_of_invoice: invoiceCurrency,
        credit_id: 0,
        invoice_payment_at: paymentAt,
      },
      {
        onSuccess: () => {
          toast.success('Payment recorded');
          reset();
          onOpenChange(false);
        },
      },
    );
  };

  return (
    <Drawer
      open={open}
      onOpenChange={(o) => {
        if (create.isPending) return;
        onOpenChange(o);
        if (!o) reset();
      }}
    >
      <DrawerContent side="right" size="md">
        <DrawerHeader>
          <DrawerTitle>Mark invoice paid</DrawerTitle>
          <DrawerDescription>Record a payment transaction against this invoice.</DrawerDescription>
        </DrawerHeader>

        <DrawerBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            {create.isError ? (
              <Alert tone="danger" title="Could not record payment">
                {errorMessage(create.error)}
              </Alert>
            ) : null}

            <Field label="Payment method" required>
              {(p) => (
                <Select value={method} onValueChange={setMethod}>
                  <SelectTrigger {...p}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>

            <Field label="Amount (before exchange rate)" required>
              {(p) => (
                <NumericInput
                  {...p}
                  min={0}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="e.g. 3190264"
                />
              )}
            </Field>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--vn-space-3)' }}>
              <Field label="Currency">
                {(p) => (
                  <Input {...p} value={currency} onChange={(e) => setCurrency(e.target.value)} placeholder="VND" />
                )}
              </Field>
              <Field label="Exchange rate">
                {(p) => (
                  <NumericInput
                    {...p}
                    min={0}
                    value={rate}
                    onChange={(e) => setRate(e.target.value)}
                    placeholder="1"
                  />
                )}
              </Field>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--vn-space-3)' }}>
              <Field label="Payment date">
                {(p) => (
                  <DateInput {...p} value={paymentAt} onChange={(e) => setPaymentAt(e.target.value)} />
                )}
              </Field>
              <Field label="Transaction fee">
                {(p) => (
                  <NumericInput {...p} min={0} value={fee} onChange={(e) => setFee(e.target.value)} />
                )}
              </Field>
            </div>

            <Field label="Note">
              {(p) => (
                <Textarea
                  {...p}
                  rows={2}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Optional payment note"
                />
              )}
            </Field>
          </div>
        </DrawerBody>

        <DrawerFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={create.isPending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} disabled={create.isPending}>
            {create.isPending ? 'Recording…' : 'Record payment'}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
